import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import HelloSign from 'hellosign-embedded';
import { BehaviorSubject, combineLatest, of } from 'rxjs';
import { catchError, finalize, tap, withLatestFrom } from 'rxjs/operators';
import { AppSettingsService, SubdomainEnum } from 'src/app/services/app-settings.service';
import { DialogService } from 'src/app/services/dialog.service';
import { RoutingService } from 'src/app/services/routing.service';
import { UserService } from 'src/app/services/shared/user.service';
import { SignatureService } from 'src/app/services/signature.service';
import { StorageService } from 'src/app/services/storage.service';
import { AlertDialogParams } from 'src/app/shared/components/alert-dialog/alert-dialog.component';
import { TerraUtils } from 'src/app/shared/TerraUtils';

@Component({
  selector: 'terra-sign-request',
  templateUrl: './sign-request.component.html',
  styleUrl: './sign-request.component.scss'
})
export class SignRequestComponent implements OnInit {
  isLoading = false;
  investmentId: number;
  uniqueIdentifier: string;
  isGp: boolean;

  helloSignWindowVariable = 'HelloSign';
  helloSignSignUrl = '';
  helloSignRef: HelloSign;

  title = '';
  message = '';
  assetName = '';
  gpCompanyName = '';
  gpFirstName = '';

  agreementSigned = false;

  isError$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isHelloSignFinished$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isCovercyFinished$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  stateHandler$ = combineLatest([this.isCovercyFinished$, this.isHelloSignFinished$]).pipe(
    withLatestFrom(this.isError$),
    tap(([[isCovercyFinished, isHellosignFinished], isError]) => {
      if (!isCovercyFinished || !isHellosignFinished) {
        return;
      }
      if (isError) {
        this.unsuccessfullySigned();
        return;
      }
      this.successfullySigned();
    })
  )

  referrerLogo$ = this.appSettings.subDomain === SubdomainEnum.GP ? this.userService.gp_referrerLogo$ : this.userService.lp_referrerLogo$;

  constructor(
    private signatureService: SignatureService,
    private router: Router,
    private route: ActivatedRoute,
    private dialogService: DialogService,
    private titleService: Title,
    private appSettings: AppSettingsService,
    private storageService: StorageService,
    private routingService: RoutingService,
    private userService: UserService
  ) { 
    this.initHelloSign();
  }

  ngOnInit() {
    this.titleService.setTitle('Sign Agreement' + TerraUtils.consts.LpPageTitleSuffix);
    this.getQueryParameters().subscribe();
    this.stateHandler$.subscribe();
  }

  logoutUser() {
    this.storageService.clear();
  }

  getQueryParameters() {
    return this.route.params.pipe(
      tap(params => {
        this.investmentId = +params.investmentId;
        this.uniqueIdentifier = params.uniqueIdentifier;
        this.isGp = params.isGp;
      })
    )
  }

  openSignUrl(): void {
    if (this.isLoading) return;
    this.isLoading = true;
    this.getSignatureRequest(Number(this.investmentId), this.uniqueIdentifier, this.isGp).pipe(
      finalize(() => {
        this.isLoading = false;
      }),
      tap(() => {
         this.openHelloSign();
      })
    ).subscribe();
  }
    
  initHelloSign() {
     this.helloSignRef = new HelloSign({
      clientId: this.appSettings.helloSignClientId,
      debug: true,
    });

      this.helloSignRef.on('sign', (helloSignEventData) => {
      const signatureId = helloSignEventData.signatureId;
      this.onAgreementSigned(signatureId).pipe(tap(_ => console.log('reqested onAgreementSigned'))).subscribe();
    });

    this.helloSignRef.on('finish', () => {
      console.log('finish signing');
    });

    this.helloSignRef.on('close', () => {
      this.isHelloSignFinished$.next(true);
    });

    this.helloSignRef.on('error', (error) => {
      console.error(error);
      this.isError$.next(true);
      this.helloSignRef.close();
    });
  }
  
  openHelloSign(): void {
    this.helloSignRef.open(this.helloSignSignUrl, {
      skipDomainVerification: this.appSettings.helloSignSkipDomainVerification
    });
  }

  getSignatureRequest(investmentId, uniqueIdentifier, isGp) {
    return this.signatureService.getSignatureRequest(investmentId, uniqueIdentifier, !!isGp)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
        tap((response: any) => {
          this.helloSignSignUrl = response.signUrl;

          this.title = response.title;
          this.message = response.message;

          this.gpCompanyName = response.gpCompanyName;
          this.gpFirstName = response.gpFirstName;
          this.assetName = response.assetName;

          this.titleService.setTitle('Sign Agreement for ' + this.assetName + TerraUtils.consts.LpPageTitleSuffix);
          this.isLoading = false;

        }
        ),
        catchError(error => {
          console.error(error);
          const dialogParams = new AlertDialogParams();

          dialogParams.title = `An error has occurred`;
          dialogParams.description = `An error has occurred, please try again or contact support`;
          dialogParams.actionLabel = `OK`;

          switch (error.status) {
            case 400:
              dialogParams.description = 'The signature was already signed';
              break;
            case 404:
              dialogParams.description = 'The signature was deleted';
              break;
          }
          this.dialogService
            .alertDialog(dialogParams)
            .afterClosed()
            .subscribe(() => {
              this.router.navigateByUrl(this.routingService.loginLpPage);
            });

          return of(error);
        })
      )

  }

  onAgreementSigned(signatureId: string) {
    return this.signatureService
      .signatureRequestSigned(this.investmentId, signatureId, !!this.isGp)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          console.log('finalized signing');
          this.isCovercyFinished$.next(true);
        }),
        tap((res) => {
          if (res) this.agreementSigned = true;
        }),
        catchError((err) => {
          console.error(err);
          this.isError$.next(true);
          return of(err);
        })
      )
  }

  private successfullySigned() {
    const dialogParams = new AlertDialogParams();
    const description = this.isGp ?
      `You can find the signed agreement in the ${this.assetName} investor's fundraising section` :
      `Shortly after all parties have signed, you will receive a signed copy in your email inbox.`;

    dialogParams.title = `You successfully signed the agreement`;
    dialogParams.description = description;
    dialogParams.actionLabel = `Close`;
    this.dialogService.alertDialog(dialogParams);
  }

  private unsuccessfullySigned() {
    const dialogParams = new AlertDialogParams();
    dialogParams.title = `An error has occurred`;
    dialogParams.description = `An error has occurred. Please try again or contact <a target="_blank" class="light link" mat-button href="//www.covercy.com/contact-us">support</a>.`;
    dialogParams.actionLabel = `Close`;
    this.dialogService.alertDialog(dialogParams)
  }
}

